/**
 * Quais rotinas o "Atualizar" deste funil adianta (#1136): o diálogo do Super
 * Admin.
 *
 * A lista vem pronta do servidor, só com as rotinas que podem rodar de novo sem
 * efeito em dobro (ativas, de agenda, criando ou movendo card pela referência,
 * neste funil). Desmarcar todas tira o botão do funil.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { useInvalidar } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, Dialogo } from '@/shared/ui';
import { Carregando, Erro } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import './rotinas-do-atualizar.css';

export function RotinasDoAtualizar({
  funil,
  nomeDoFunil,
  aoFechar,
  aoSalvar,
}: {
  funil: string;
  nomeDoFunil: string;
  aoFechar: () => void;
  aoSalvar: (marcadas: number) => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const consulta = useQuery({
    queryKey: chaves.atualizarDoFunil(funil),
    queryFn: ({ signal }) =>
      api!.configuracaoDoAtualizar({ caminho: { id: funil }, sinal: signal }),
    enabled: api !== null,
  });
  const [marcadas, definirMarcadas] = useState<Set<string> | null>(null);
  useEffect(() => {
    // Só da leitura feita ao abrir: o cache de uma abertura anterior pode estar velho.
    if (
      consulta.data &&
      consulta.isFetchedAfterMount &&
      !consulta.isFetching &&
      marcadas === null
    ) {
      definirMarcadas(new Set(consulta.data.rotinas.filter((r) => r.marcada).map((r) => r.id)));
    }
  }, [consulta.data, consulta.isFetchedAfterMount, consulta.isFetching, marcadas]);

  const salvar = useMutation({
    mutationFn: (ids: string[]) =>
      api!.definirConfiguracaoDoAtualizar({ caminho: { id: funil }, corpo: { rotinas: ids } }),
    onSuccess: (r) => {
      invalidar([chaves.funis(), chaves.atualizarDoFunil(funil)]);
      aoSalvar(r.rotinas.filter((x) => x.marcada).length);
    },
  });

  const rotinas = consulta.data?.rotinas ?? [];
  return (
    <Dialogo
      titulo={`Rotinas do "Atualizar" · ${nomeDoFunil}`}
      aoFechar={aoFechar}
      largura="padrao"
      descricaoId="rotinas-atualizar-explica"
      botoes={
        <>
          <Botao variante="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="primario"
            desabilitado={marcadas === null || salvar.isPending}
            motivo={salvar.isPending ? 'Salvando…' : 'Carregando as rotinas…'}
            onClick={() => marcadas && salvar.mutate([...marcadas])}
          >
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Botao>
        </>
      }
    >
      <p id="rotinas-atualizar-explica" className="rotinas-atualizar__explica">
        O botão "Atualizar" do funil roda agora só as rotinas marcadas, sem mudar o horário delas.
        Aparecem aqui só as que podem rodar de novo sem repetir nada: ativas, de horário, que criam
        ou movem cards deste funil pela referência. Sem nenhuma marcada, o funil fica sem o botão.
      </p>
      {(consulta.isPending || (consulta.data && marcadas === null)) && (
        <Carregando forma="lista" linhas={3} rotulo="Carregando as rotinas" />
      )}
      {consulta.isError && (
        <Erro
          erro={consulta.error}
          titulo="Não foi possível ler as rotinas"
          aoRepetir={() => void consulta.refetch()}
        />
      )}
      {consulta.data && rotinas.length === 0 && (
        <p className="rotinas-atualizar__vazio">
          Nenhuma rotina deste funil pode entrar no botão. Uma rotina do modelo "Rotina agendada",
          ativa, que crie ou mova cards deste funil pela referência, aparece aqui.
        </p>
      )}
      {rotinas.length > 0 && marcadas && (
        <ul className="rotinas-atualizar__lista">
          {rotinas.map((r) => (
            <li key={r.id}>
              <label className="rotinas-atualizar__rotina">
                <input
                  type="checkbox"
                  checked={marcadas.has(r.id)}
                  onChange={(e) => {
                    const proximo = new Set(marcadas);
                    if (e.target.checked) proximo.add(r.id);
                    else proximo.delete(r.id);
                    definirMarcadas(proximo);
                  }}
                />
                <span className="rotinas-atualizar__nome">
                  {r.nome ?? (r.disponivel ? 'Rotina sem nome' : 'Rotina que não existe mais')}
                  {!r.disponivel && (
                    <span className="rotinas-atualizar__aviso">
                      Pausada ou fora da regra: não roda pelo botão até voltar a valer.
                    </span>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {salvar.isError && <Faixa tom="bloqueado">{recadoDeErro(salvar.error)}</Faixa>}
    </Dialogo>
  );
}
